import type { WorkspacePersonaRole } from '@myplayer/rbac';
import { IconCheck, IconEdit, IconExternalLink, IconRefresh, IconTrash } from '@myplayer/ui';
import { useState, type ReactElement } from 'react';
import {
  ActionOverflowMenu,
  Badge,
  Button,
  CardHeader,
  CardIcon,
  CardRow,
  CardTitle,
  Input,
  ModalDialog,
  Select,
  Switch,
} from './components/ui/primitives.js';
import {
  DEFAULT_GOOGLE_ANALYTICS_ACCOUNTS,
  findConnectedStreamMetadata,
  formatGoogleAnalyticsBadgeLabel,
  formatGoogleAnalyticsBadgeVariant,
  formatGoogleAnalyticsPlainStatus,
  type GoogleAnalyticsAccountOption,
  type GoogleAnalyticsConfigModalProps,
  type GoogleAnalyticsIntegrationState,
} from './integrations-helpers.js';
import { formatCapabilityRestrictionNotice } from './rbac-ui.js';

export * from './integrations-helpers.js';

export function GoogleAnalyticsCardHeader(p: {
  readonly gaState: GoogleAnalyticsIntegrationState;
  readonly canManageIntegrations: boolean;
  readonly personaRole: WorkspacePersonaRole | undefined;
  readonly busy: boolean;
  readonly onOpenEditModal: () => void;
  readonly onSendTestEvent: () => void;
  readonly onDisconnect: () => void;
  readonly onRemoveTile?: () => void;
}): ReactElement {
  const isConfigured = p.gaState.measurementId.length > 0 && p.gaState.hasApiSecret;
  return (
    <CardHeader>
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <CardIcon>
          <IconExternalLink />
        </CardIcon>
        <CardTitle>Google Analytics 4</CardTitle>
        <Badge variant={formatGoogleAnalyticsBadgeVariant(p.gaState)} data-testid="ga-status-badge">
          {formatGoogleAnalyticsBadgeLabel(p.gaState)}
        </Badge>
      </div>
      {p.canManageIntegrations ? (
        <div className="flex shrink-0 items-center gap-1.5">
          <Button
            type="button"
            size="sm"
            variant={isConfigured ? 'secondary' : 'primary'}
            disabled={p.busy}
            data-testid="edit-ga-integration-btn"
            aria-label={isConfigured ? 'Edit Google Analytics' : 'Connect Google Analytics'}
            onClick={p.onOpenEditModal}
          >
            <IconEdit />
            <span>{isConfigured ? 'Edit' : 'Connect Google Analytics'}</span>
          </Button>
          {(isConfigured || p.onRemoveTile !== undefined) && (
            <ActionOverflowMenu
              label="More actions for Google Analytics"
              disabled={p.busy}
              data-testid="ga-integration-overflow"
            >
              {isConfigured && (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={p.busy}
                  data-testid="send-ga-test-event-btn"
                  aria-label="Send test event"
                  onClick={p.onSendTestEvent}
                >
                  <IconRefresh />
                  <span>Send test event</span>
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant="destructive"
                disabled={p.busy}
                data-testid="disconnect-ga-integration-btn"
                aria-label={
                  isConfigured ? 'Disconnect Google Analytics' : 'Remove Google Analytics'
                }
                onClick={isConfigured ? p.onDisconnect : p.onRemoveTile}
              >
                <IconTrash />
                <span>{isConfigured ? 'Disconnect' : 'Remove tile'}</span>
              </Button>
            </ActionOverflowMenu>
          )}
        </div>
      ) : (
        <span
          role="status"
          data-testid="integrations-readonly-notice"
          className="text-[11px] font-medium text-fg-muted"
        >
          {formatCapabilityRestrictionNotice({
            capability: 'domain:manage',
            actionSummary: 'configure Google Analytics and workspace integrations',
            callerRole: p.personaRole,
          })}
        </span>
      )}
    </CardHeader>
  );
}

export function GoogleAnalyticsTileRows(p: {
  readonly gaState: GoogleAnalyticsIntegrationState;
  readonly accounts: readonly GoogleAnalyticsAccountOption[];
  readonly canManageIntegrations: boolean;
  readonly submitting: boolean;
  readonly testingEvent: boolean;
  readonly onToggleEnabled: (nextEnabled: boolean) => void;
}): ReactElement {
  const isConfigured = p.gaState.measurementId.length > 0 && p.gaState.hasApiSecret;
  const plainStatus = formatGoogleAnalyticsPlainStatus(p.gaState);
  const connectedMeta = findConnectedStreamMetadata(p.accounts, p.gaState.measurementId);
  const propName = p.gaState.propertyName ?? connectedMeta?.propertyName ?? null;
  const strName = p.gaState.streamName ?? connectedMeta?.streamName ?? null;

  return (
    <div
      data-testid="google-analytics-tile"
      className="divide-y divide-subtle border-y border-subtle"
    >
      <CardRow label="Integration">
        <span className="text-[12px] font-semibold text-fg">Google Analytics 4</span>
      </CardRow>
      <CardRow label="Status">
        <span data-testid="ga-plain-status" className="text-[12px] text-fg">
          {plainStatus}
        </span>
      </CardRow>
      {isConfigured && (
        <CardRow label="Connected property">
          <span data-testid="ga-connected-property" className="text-[12px] font-medium text-fg">
            {propName
              ? `${propName}${strName ? ` · ${strName}` : ''} (${p.gaState.measurementId})`
              : p.gaState.measurementId}
          </span>
        </CardRow>
      )}
      <CardRow label="Send scan events">
        <Switch
          id="ga-tile-enabled-switch"
          aria-label="Enable Google Analytics"
          checked={p.gaState.enabled}
          disabled={!p.canManageIntegrations || !isConfigured || p.submitting || p.testingEvent}
          onChange={(e) => p.onToggleEnabled(e.target.checked)}
          label={p.gaState.enabled ? 'On' : 'Off'}
        />
      </CardRow>
      <CardRow label="Measurement ID">
        <span
          className={
            p.gaState.measurementId
              ? 'font-mono text-[12px] font-semibold text-fg'
              : 'text-[12px] text-fg-muted'
          }
        >
          {p.gaState.measurementId || 'Not set'}
        </span>
      </CardRow>
      <CardRow label="API secret">
        {p.gaState.hasApiSecret ? (
          <span data-testid="ga-secret-masked" className="text-[12px] text-fg-secondary">
            Saved · Hidden for security
          </span>
        ) : (
          <span className="text-[12px] text-fg-muted">Not set</span>
        )}
      </CardRow>
    </div>
  );
}

export function GoogleAnalyticsConfigModal(props: GoogleAnalyticsConfigModalProps): ReactElement {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const accounts = props.accounts ?? DEFAULT_GOOGLE_ANALYTICS_ACCOUNTS;
  const activeAccount =
    accounts.find((a) => a.accountId === (props.selectedAccountId ?? '')) ?? accounts[0] ?? null;
  const properties = activeAccount?.properties ?? [];
  const activeProperty =
    properties.find((p) => p.propertyId === (props.selectedPropertyId ?? '')) ??
    properties[0] ??
    null;
  const streams = activeProperty?.streams ?? [];
  const labelCls = 'mb-1 block text-[12px] font-medium text-fg-secondary';

  return (
    <ModalDialog
      open={props.open}
      title="Connect Google Analytics"
      subtitle={`Connect Google Analytics 4 for ${props.workspaceId}`}
      icon={<IconExternalLink />}
      presentation="center"
      maxWidthClassName="max-w-2xl"
      submitting={props.submitting}
      onClose={props.onClose}
      data-testid="ga-integration-modal"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={props.submitting}
            onClick={props.onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="ga-integration-form"
            variant="primary"
            size="sm"
            disabled={props.submitting}
            data-testid="save-ga-integration-btn"
            aria-label="Save Google Analytics"
          >
            <IconCheck />
            <span>Save</span>
          </Button>
        </>
      }
    >
      <form
        id="ga-integration-form"
        aria-label="Configure Google Analytics"
        noValidate
        onSubmit={props.onSubmit}
        className="space-y-4"
      >
        <Switch
          id="ga-modal-enabled-switch"
          checked={props.draftEnabled}
          onChange={(e) => props.setDraftEnabled(e.target.checked)}
          disabled={props.submitting}
          label={`Send QR scan events to Google Analytics (${props.draftEnabled ? 'On' : 'Off'})`}
        />
        <div
          data-testid="ga-oauth-picker-section"
          className="space-y-3 rounded-control border border-subtle bg-surface-subtle p-3"
        >
          {!props.googleConnected ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-[12px] text-fg-secondary">
                Sign in with Google to pick your Google Analytics account, property, and data
                stream.
              </p>
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={props.submitting}
                data-testid="ga-google-signin-btn"
                onClick={props.onConnectGoogleAccount}
              >
                Connect Google Analytics
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {[
                {
                  id: 'ga-account-select',
                  label: 'Account',
                  value: activeAccount?.accountId ?? '',
                  onChange: props.onSelectAccount,
                  opts: accounts.map((a) => ({ value: a.accountId, label: a.accountName })),
                },
                {
                  id: 'ga-property-select',
                  label: 'Property',
                  value: activeProperty?.propertyId ?? '',
                  onChange: props.onSelectProperty,
                  opts: properties.map((p) => ({ value: p.propertyId, label: p.propertyName })),
                },
                {
                  id: 'ga-stream-select',
                  label: 'Data stream',
                  value: props.selectedStreamId || (streams[0]?.streamId ?? ''),
                  onChange: props.onSelectStream,
                  opts: streams.map((s) => ({
                    value: s.streamId,
                    label: `${s.streamName} (${s.measurementId})`,
                  })),
                },
              ].map((field) => (
                <div key={field.id}>
                  <label htmlFor={field.id} className={labelCls}>
                    {field.label}
                  </label>
                  <Select
                    id={field.id}
                    data-testid={field.id}
                    value={field.value}
                    disabled={props.submitting}
                    onChange={(e) => field.onChange?.(e.target.value)}
                  >
                    {field.opts.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                </div>
              ))}
            </div>
          )}
        </div>
        <details
          open={advancedOpen}
          data-testid="ga-advanced-manual-section"
          onToggle={(e) => setAdvancedOpen((e.currentTarget as HTMLDetailsElement).open)}
          className="rounded-control border border-subtle p-3"
        >
          <summary
            data-testid="ga-enter-manually-toggle"
            className="cursor-pointer text-[12px] font-medium text-fg-secondary select-none"
          >
            Advanced · Enter manually
          </summary>
          <div className="mt-3 space-y-3">
            <div>
              <label htmlFor="ga-measurement-id" className={labelCls}>
                Measurement ID
              </label>
              <Input
                id="ga-measurement-id"
                type="text"
                value={props.draftMeasurementId}
                onChange={(e) => props.setDraftMeasurementId(e.target.value)}
                placeholder="G-1A2B3C4D5E"
                disabled={props.submitting}
                className="font-mono"
              />
            </div>
            <div>
              <label htmlFor="ga-api-secret" className={labelCls}>
                API secret
              </label>
              <Input
                id="ga-api-secret"
                type="password"
                autoComplete="new-password"
                value={props.draftApiSecret}
                onChange={(e) => props.setDraftApiSecret(e.target.value)}
                placeholder={
                  props.hasExistingSecret
                    ? 'Saved — enter a new secret to replace it'
                    : 'Enter Measurement Protocol API secret'
                }
                disabled={props.submitting}
              />
            </div>
          </div>
        </details>
        {props.errorMessage !== null && (
          <p
            role="alert"
            data-testid="ga-modal-error"
            className="rounded-control border border-status-error-border bg-status-error-bg p-2.5 text-[12px] font-medium text-status-error"
          >
            {props.errorMessage}
          </p>
        )}
      </form>
    </ModalDialog>
  );
}
